<script lang="ts">
  import type { Snippet } from 'svelte';
  import { Link } from 'svelte-routing';
  import Sidebar from './Sidebar.svelte';
  import AppFooter from './AppFooter.svelte';
  import UserMenu from './UserMenu.svelte';
  import { user } from '../lib/stores/auth';

  interface Props {
    /** Route content rendered inside the main area. Owns its own globe canvas. */
    children: Snippet;
  }

  let { children }: Props = $props();

  /**
   * Sidebar state. Persisted in localStorage so the user's preference
   * survives reloads. `mobileOpen` is the hamburger overlay (only relevant
   * below the responsive breakpoint).
   */
  const SIDEBAR_EXPANDED_KEY = 'atlasguess.sidebar.expanded';
  let expanded = $state<boolean>(loadInitialExpanded());
  let mobileOpen = $state<boolean>(false);

  function loadInitialExpanded(): boolean {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return stored === null ? true : stored === 'true';
  }

  function toggleExpanded(): void {
    expanded = !expanded;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(expanded));
    }
  }

  function openMobile(): void {
    mobileOpen = true;
  }

  function closeMobile(): void {
    mobileOpen = false;
  }
</script>

<div class="app-layout">
  <Sidebar
    {expanded}
    {mobileOpen}
    onToggleExpand={toggleExpanded}
    onCloseMobile={closeMobile}
  />

  <!-- Mobile-only hamburger. Sits above the route content in the top-left
       corner. Hidden on desktop (sidebar is always visible there). -->
  <button
    class="app-layout__hamburger"
    aria-label="Open menu"
    onclick={openMobile}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
      <line x1="4" y1="7" x2="20" y2="7" stroke-linecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke-linecap="round" />
      <line x1="4" y1="17" x2="20" y2="17" stroke-linecap="round" />
    </svg>
  </button>

  <!-- Mobile-only auth pill — bottom-left corner, just above the
       AppFooter drag-hint chrome. Same dark-chrome look as the
       hamburger so the two corners read as a pair. Logged-out shows
       a Login link with a tiny user icon; logged-in shows the shared
       UserMenu (donor icon + truncated username + chevron) and opens
       the same dropdown the sidebar / TopNav use. Hidden on desktop —
       the sidebar footer covers auth there. -->
  <div class="app-layout__auth-pill">
    {#if $user}
      <UserMenu user={$user} placement="top-start" triggerVariant="pill" />
    {:else}
      <Link
        to="/login"
        getProps={() => ({
          class: 'app-layout__auth-pill-link',
          'aria-label': 'Sign in',
        })}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <circle cx="12" cy="9" r="3.5" />
          <path d="M5 20 C5 16 8 14 12 14 S19 16 19 20" />
        </svg>
        <span>Login</span>
      </Link>
    {/if}
  </div>

  <main
    class="app-layout__main"
    class:app-layout__main--sidebar-collapsed={!expanded}
  >
    {@render children()}
  </main>

  <AppFooter sidebarExpanded={expanded} />
</div>
